import {keys, find, includes, map, isEmpty, intersection} from 'lodash-es';
import moment from 'moment';

import {onboardingPath, signUpRoot} from 'static/three-oh/src/modules/constants/routePaths';
import {
  startOfSchoolYear,
  endOfSchoolYear,
  laborDay,
} from 'static/three-oh/src/modules/utils/dateHelpers';

import canvasImage from './assets/sync-canvas.png';
import classlinkImage from './assets/sync-classlink.png';
import cleverImage from './assets/sync-clever.png';
import googleImage from './assets/sync-google.png';

export const providerImages = {
  'google-oauth2': googleImage,
  clever: cleverImage,
  canvas: canvasImage,
  classlink: classlinkImage,
};

export const SUPPORTED_AUTH_PROVIDERS = keys(providerImages);

const currentSchoolYear = startOfSchoolYear().year();

/**
 * Returns a list of time windows.
 *
 * Note that window edges are one day further on both sides to accommodate
 *  non-inclusive nature of moment().isBetween(a, b)
 *  https://momentjs.com/docs/#/query/is-between/
 *
 * @return {Array.<Array.<moment>>}
 */
export const getTimeWindows = () => {
  // Define three time windows:
  // - Aug 3 through Aug 16
  // - Aug 17 through Labor day.
  // - Day after Labor day through the end of the school year.
  const window1Start = moment(`${currentSchoolYear}-08-02`);
  const window1End = moment(window1Start).add(2, 'weeks').add(1, 'day');
  const window2Start = moment(window1End).subtract(1, 'day');
  const window2End = laborDay();
  const window3Start = laborDay().subtract(1, 'day');
  const window3End = endOfSchoolYear();
  return [
    [window1Start, window1End],
    [window2Start, window2End],
    [window3Start, window3End],
  ];
};

/**
 * Returns true if one of the dates passed as an argument is within current time window.
 * @param {Array.<string | moment>} dates
 * @return {Boolean}
 */
export const isWithinCurrentTimeWindow = (dates) => {
  const windows = getTimeWindows();
  const today = moment();
  const currentWindow = find(windows, (window) => {
    return today.isBetween(...window);
  });
  if (!currentWindow) {
    return false;
  }
  return !!find(dates, (date) => {
    return moment(date).isBetween(...currentWindow);
  });
};

/**
 * Determine whether welcome back mesage should be rendered based on user's data.
 *
 * @param {Object} user - signed in user.
 * @param {Array.object} eventRecords - List of generic events of the same type.
 * @param {Array.object} rosterInfoList - List of roster sync event records
 * @return {Boolean}
 */
export const shouldWelcomeBackTeacher = (user, eventRecords, rosterInfoList) => {
  // Show welcome back message when:
  // - within time windows checks.
  // - user hasn't seen welcome back message in the current time window.
  // - user can roster.
  // - user is not onboarding.
  // - user hasn't synced yet this school year.
  const joinedBeforeCurrentYear = moment(user.date_joined).isBefore(startOfSchoolYear());
  const userSyncedAfterBeginningOfSchool = find(rosterInfoList, (rosterEntry) => {
    return moment(rosterEntry.time_started).isAfter(`${currentSchoolYear}-07-01`);
  });
  const supportedCurrentLogin = includes(SUPPORTED_AUTH_PROVIDERS, user.auth_type);
  const authProviders = map(user.social_auth, 'provider');
  const userCanRoster = !isEmpty(
    intersection(SUPPORTED_AUTH_PROVIDERS, authProviders)
  ) && supportedCurrentLogin;
  // Temporary workaround until all teacher have onboarded and have corresponding
  // user event record.
  const isUserSettingUp = (
    window.location.pathname.includes(onboardingPath) ||
    window.location.pathname.includes(signUpRoot) ||
    window.location.pathname.includes('/merge-accounts/')
  );
  // August third of the current school year is when we first should welcome back teachers.
  const isOnOrAfterAugustThird = moment().isSameOrAfter(moment(`${currentSchoolYear}-08-03`));
  const eventDates = map(eventRecords, 'date_created');
  const seenWelcomeBackInCurrentTimeWindow = isWithinCurrentTimeWindow(eventDates);
  return (
    isOnOrAfterAugustThird &&
    userCanRoster &&
    !isUserSettingUp &&
    joinedBeforeCurrentYear &&
    !userSyncedAfterBeginningOfSchool &&
    !seenWelcomeBackInCurrentTimeWindow
  );
};
